import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, request } from '../api/client';
import { Field, Modal } from '../ui/components';

const MIN = 12;

/**
 * The signed-in user changes their own password (current password required, checked by the server); their other
 * sessions are signed out by the server. A centred dialog over the page: as a drawer inside the top bar its top — title
 * and "Current password" — was cut off above the window, so the button never enabled (user report 2026-10-10).
 */
export function ChangePassword({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const save = useMutation({
    mutationFn: () =>
      request('POST', '/api/v1/auth/password', { current_password: current, new_password: next }, () => null),
  });
  const mismatch = repeat.length > 0 && repeat !== next;
  const tooShort = next.length > 0 && next.length < MIN;
  const error =
    save.error instanceof ApiRequestError
      ? t(`account.password.errors.${save.error.code}` as 'account.password.errors.INVALID_CREDENTIALS', {
          defaultValue: t('account.password.errors.generic'),
        })
      : save.error
        ? t('account.password.errors.generic')
        : null;
  return (
    <Modal
      title={t('account.password.title')}
      onClose={onClose}
      size="small"
      footer={
        save.isSuccess ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!current || next.length < MIN || next !== repeat || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('account.password.save')}
          </button>
        )
      }
    >
      {save.isSuccess ? (
        <p className="form-ok">{t('account.password.done')}</p>
      ) : (
        <form className="grid" onSubmit={(e) => e.preventDefault()}>
          <Field label={t('account.password.current')}>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label={t('account.password.new')} hint={t('account.password.hint', { min: MIN })}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label={t('account.password.repeat')}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </Field>
          {tooShort && <p className="form-error">{t('account.password.hint', { min: MIN })}</p>}
          {mismatch && <p className="form-error">{t('account.password.mismatch')}</p>}
          {error && <p className="form-error">{error}</p>}
        </form>
      )}
    </Modal>
  );
}
